// Import react elements
import { createContext, useContext, useState, useEffect } from "react";

// Import the stored app config getter
import { GetAppConfig } from "global/config/AppConfigStore";

// Import strich api calls
import {
    getUserAccountData,
    getConnections,
    getStores,
    getShippingMethods,
    getOrderStatuses,
    getCoreCurrencies,
    getCoreCountries,
    getCoreRegions,
    getFulfilmentCountries,
    getPodProducts,
    getRateCard,
    getSubscriptionLevels,
    getSubscriptionPackages,
    getFulfilmentServicesBy
} from "api/APIStrichCalls";

// Import context function
import { AuthContext } from "global/context/AuthContext";

// Import components
import  OpenStoreButton  from "global/components/OpenStoreButton";



// Create user context
export const UserContext = createContext();



// UserProvider is a Context Provider component that wraps the entire app
// It gives all components inside access to user-related data and functions (e.g. email, username, password).
export const UserProvider = ({ children }) => {



    // Get the token from AuthContext
    const { token, authReady } = useContext(AuthContext);



    // Set a state for the user data retrieved from the API
    const [userData, setUserData] = useState("");

    const [userDataErrorMessage, setUserDataErrorMessage] = useState(null); 

    // Set a state for the connections data retrieved from the API
    const [connectionsData, setConnectionsData] = useState([]);

    // Set a state for the stores retrieved from the API
    const [stores, setStores] = useState([]);

    // Set a state for the shipping methods data retrieved from the API
    const [shippingMethods, setShippingMethods] = useState([]);

    // Set a state for storing the order statuses retrieved from the API
    const [orderStatuses, setOrderStatuses] = useState([]);

    // Set a state for storing the core currencies for display in prices and setting home account currency
    const [currencies, setCurrencies] = useState([]);

    // Set a state for storing the core countries for creating an account retrieved from the API
    const [countries, setCountries] = useState([]);

    // Set a state for storing the core regions (groups of coutntries mostly used for pricing)
    const [regions, setRegions] = useState([]);

    // Set a state for storing the core countries for fulfilment retrieved from the API
    const [fulfilmentCountries, setFulfilmentCountries] = useState([]);

    // Set a state for storing the canonical strich products retrieved from the API
    const [canonicalProducts, setCanonicalProducts] = useState([]);

    // Set a state for storing the canonical strich products grouped by department
    const [groupedCanonicalProducts] = useState([]);

    // Set a state to store rate card data retrieved from the API
    const [rateCard, setRateCard] = useState("");

    // Set a state to store subscription level data retrieved from the API
    const [subscriptionLevels, setSubscriptionLevels] = useState([]);

    // Set a state to store subscription level data retrieved from the API
    const [subscriptionPackages, setSubscriptionPackages] = useState([]);

    // Set a state for storing fulfilment service codes 
    const [serviceCodes, setServiceCodes] = useState([]);


    // Read the loaded runtime config only when this function is called
    const config = GetAppConfig();

    // Read the configured store url
    const storeURL = config?.storeUrl || "";


    // Fulfilments services
    // Other stuffs...


    const refreshConnectionsData = async () => {

        // Get the users connections details
        const connectionsResponse = await getConnections(token);

            // Hide system portal stores from the Stores page list
            const visibleConnections = connectionsResponse?.results?.filter((con) => con.conntype !== "STRICH_PORTAL");

			// Create updated array
			const connectionsWithStoreLinkDiv = visibleConnections?.map((con) => {

                // Check if there is an alias (needed for the store to open)
                if(con.alias) {

                    // Return the connection with the added field 
                    return {
                        ...con,
                        storeUrl: storeURL + con.alias,

                        storeLink: 
                            <OpenStoreButton alias={con.alias} buttonTitle={"View Store"} />,              
                    };

                }
                
                // Return as is if there is no alias
                return con;
                    
                });
                            
        // Set the state with the updated results
        setConnectionsData(connectionsWithStoreLinkDiv);

    };


    // Reusable fetch function
    const refreshUserData = async () => {

        // Check if there's no token or if the authReady flag is false
        if (!authReady || !token)

            // Return early
            return;

        // We have a token and the AuthReady flag is true
        // Make the API call to get and store the response

        // Get the user's account details
        const accountDataResponse = await getUserAccountData(token, setUserData);

        if (!accountDataResponse.success) {

            // Set the error message
            setUserDataErrorMessage(accountDataResponse.error_message);

        }

        refreshConnectionsData();

        // Get the users stores
        getStores(token, setStores);

        // Get the available shipping methods
        getShippingMethods(token, setShippingMethods);

        // Get order statuses
        getOrderStatuses(token, setOrderStatuses);

        // Get core countries
        getCoreCurrencies(token, setCurrencies);

        // Get core countries
        getCoreCountries(token, setCountries);

        // Get core regions
        getCoreRegions(token, setRegions);

        // Get fulfilment countries
        getFulfilmentCountries(token, setFulfilmentCountries);
    
        // Get the rate card
        const rateCardResponse = await getRateCard(token);

            if (rateCardResponse.success) {

                setRateCard(rateCardResponse);

            }

        // Get the subscription levels using the account currency
        getSubscriptionLevels(token, setSubscriptionLevels);

        // Get the subscription packages using the account currency
        getSubscriptionPackages(token, setSubscriptionPackages, accountDataResponse?.results?.currency);
    
        // Get the canonical products
        const canonicalProductsResponse = await getPodProducts(token, accountDataResponse?.results?.country);

        // Use the canonical products returned by the API
        const activeCanonicalProducts = canonicalProductsResponse?.results || [];

        // Set the canonical products from the API response
        setCanonicalProducts(activeCanonicalProducts);

        // Get Fulfilment Service Codes
        const servicesResponse = await getFulfilmentServicesBy(token);

            if (servicesResponse.success) {

                setServiceCodes(servicesResponse.results || []);

            }

    };


/*
    // Call function to get the canonical Strich products, group them, and store the data - plus add the custom data to each product so we can create preview images
    const fetchAndGroupCanonocialProducts = async () => {
    
        try {

            // Make api call
            const response = await getPodProducts(token);

            // If successful and there's at least one product in the response
            if (response?.success && response.results && Object.keys(response.results).length > 0) {

                // map through the canonical products
                // for each product
                // find the matching customcode product in customizr products
                // if the product is a parent (wall print) take the first product that matches at the front of the code FIDO_WALLART_LUSTRE => FIDO_WALLART_LUSTRE_A5_PORTRAIT
                // append the entire print_areas array from matching customizr product to the canonical product
                // then filter all products by department


                // Now sort the products into departments - group by department (in item specifics)
                const groupProductsByDepartment = response.results.reduce((acc, item) => {

                    // Get the object in item specifics for 'department'
                    const departmentObject = item.item_specifics?.find(obj => Object.hasOwn(obj, "Department"));

                    // Get the department value if there is one
                    const departmentValue = departmentObject?.["Department"];

                    // Use Department value if there is one or fallback to "Uncategorised"
                    const departent = departmentValue
                        ? departmentValue
                        : "Uncategorised";

                    // Initialize the group if it doesn't exist
                    if (!acc[departent]) acc[departent] = [];

                    // Add the item to the correct departement group
                    acc[departent].push(item);

                    return acc;

                    // Start with an empty object
                }, {});


                // Remove any inactive products before setting to state - shouldn't need to do this because the call should only return active products
                const activeCanonicalProducts = response.results.filter((product) => !product.active || product.active === "N" )

                // Set the canonicalProducts to the filtered api response
                setCanonicalProducts(activeCanonicalProducts);

                // Set the groupedCanonicalProducts to the grouped products - used in POD products
                setGroupedCanonicalProducts(groupProductsByDepartment);

            } 

        } catch (err) {

            

        }

    };

    */



    // Call it once on load
    useEffect(() => {

        refreshUserData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, authReady]);

    

    return (

        // The AuthContext.Provider makes user's account data (like their eamil, username and password) 
        // available to all child components that use the UserContext via useContext().
        <UserContext.Provider value={{
            userData,
            setUserData,
            userDataErrorMessage,
            connectionsData,
            stores,
            setConnectionsData,
            refreshUserData,
            refreshConnectionsData,
            shippingMethods,
            canonicalProducts,
            groupedCanonicalProducts,
            orderStatuses,
            currencies,
            countries,
            regions,
            fulfilmentCountries,
            rateCard,
            subscriptionLevels,
            subscriptionPackages,
            serviceCodes
        }}>

            {/* This renders the entire app inside the provider, giving everything access to user */}
            {children}

        </UserContext.Provider>

    );



};